import { useEffect } from 'react';
import { strings } from '@/shared/strings';
import { ConfirmSheet } from './ConfirmSheet';
import { useConfirmStore } from './confirmStore';

/**
 * Draws the question in `confirmStore`, in the one shared confirmation. The
 * request of 5 October 2026 (third), section 2.
 *
 * Mounted once, in `App`, after the routes and beside the toasts: outside every
 * scroller, page change and drawer, and later in the document than any dialog a
 * question can be asked from, which is what puts it on top of them (every
 * overlay shares one z-index, so the order of the document decides).
 *
 * The sheet stays up, busy, until the command settles, and then goes whichever
 * way it settled; a refusal is a toast. Cancel, the close button, a swipe and
 * the backdrop send nothing.
 */
export function ConfirmHost() {
  const request = useConfirmStore((state) => state.request);
  const busy = useConfirmStore((state) => state.busy);
  const accept = useConfirmStore((state) => state.accept);
  const cancel = useConfirmStore((state) => state.cancel);
  const open = request !== null;

  /*
   * Escape belongs to the question while it is open. The dialog, sheet, drawer
   * or page underneath listens for Escape on the document too, and would close
   * along with the question and take a half-filled form with it. So the key is
   * caught on its way down, before any of them hears it, and it closes the
   * question alone. While the command runs it does nothing.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      const state = useConfirmStore.getState();
      if (!state.busy) state.cancel();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  if (request === null) return null;

  return (
    <ConfirmSheet
      // A new question is a new sheet: the lock of the last one does not carry over.
      key={request.id}
      title={request.title}
      statement={request.statement}
      confirmLabel={request.confirmLabel}
      cancelLabel={request.cancelLabel}
      closeLabel={strings.sheet.close}
      busy={busy}
      onConfirm={() => void accept()}
      onCancel={cancel}
    />
  );
}
